// src/PaymentHandler.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import jsPDF from "jspdf";


/**
 * ✅ Villa PaymentHandler
 * Works with selectedPlotData from Map.jsx:
 * {
 *  firestoreId, villaId, villaNumber, facing, configuration,
 *  builtUpAreaSqft, landAreaSqft, premiumCharges, inclusivePrice,
 *  floorPlan, status
 * }
 */
const PaymentHandler = ({
  plotData,
  plotStatus,
  projectId, // you pass selectedPlotId here (villaId)
  setPlotStatus,
  closeParentPopup,
}) => {
  // ---------------------- CONFIG ----------------------
  const VILLA_COLLECTION = "ClarksExoticaVilla";
  const BOOKINGS_COLLECTION = "villaBookings";
  const INVOICES_COLLECTION = "villaInvoices";

  // Razorpay amount
  // ✅ Prefer env (you already use it), but make sure it is in PAISE.
  // If env is in RUPEES, set REACT_APP_ADVANCE_AMNTS_IN_PAISE="false" or just store paise directly.
  const ADVANCE_RUPEES_FALLBACK = 9999900;

  const isEnvPaise = String(process.env.REACT_APP_ADVANCE_AMNTS_IN_PAISE || "true").toLowerCase() === "true";
  const envAmt = Number(process.env.REACT_APP_ADVANCE_AMNTS || 0);

  const advanceAmountPaise = useMemo(() => {
    if (Number.isFinite(envAmt) && envAmt > 0) {
      return isEnvPaise ? Math.round(envAmt) : Math.round(envAmt * 100);
    }
    return Math.round(ADVANCE_RUPEES_FALLBACK * 100);
  }, [envAmt, isEnvPaise]);

  const advanceAmountRupees = useMemo(() => Math.round(advanceAmountPaise / 100), [advanceAmountPaise]);

  // The most reliable doc id for update
  const villaDocId = plotData?.firestoreId || null;

  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [showBookingPopup, setShowBookingPopup] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const [showConfirmationSection, setShowConfirmationSection] = useState(false);
  const [showKYCSection, setShowKYCSection] = useState(false);
  const [showErrorSection, setShowErrorSection] = useState(false);

  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validationError, setValidationError] = useState("");
  const [bookingDetails, setBookingDetails] = useState(null);

  const [hasApplicant2, setHasApplicant2] = useState(false);
  const [formData, setFormData] = useState({
    name: auth.currentUser?.displayName || "",
    applicant2Name: "",
    email: auth.currentUser?.email || "",
    _level: "L2",
    mobileNumber: auth.currentUser?.phoneNumber || "",
    pan: "",
    applicant2Pan: "",
    termsAgreed: false,
    privacyAgreed: false,
    source: "Direct",
    sourceDetails: "",
  });

  const [aadhaarUrl, setAadhaarUrl] = useState("");
  const [panUrl, setPanUrl] = useState("");
  const [aadhaar2Url, setAadhaar2Url] = useState("");
  const [pan2Url, setPan2Url] = useState("");

  const timeoutIdRef = useRef(null);




  // Cloudinary
  const cloudName = "doi5elbsg";
  const uploadPreset = "essenndocs";

  // ---------------------- SCRIPT LOAD ----------------------
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;

    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => {
      setRazorpayLoaded(false);
      setErrorMessage("Failed to load payment gateway. Please try again later.");
      setShowBookingPopup(true);
      setShowErrorSection(true);
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    };
  }, []);

  // ---------------------- HELPERS ----------------------
  const formatINR = (v) => {
    const n = Number(v);
    if (!n || Number.isNaN(n)) return "N/A";
    return `₹${n.toLocaleString("en-IN")}`;
  };

  const safeLower = (v) => String(v || "").toLowerCase();

  const handleBookNow = async () => {
    const st = safeLower(plotStatus);
    if (st !== "available") {
      setErrorMessage("This villa is not available for booking.");
      setShowBookingPopup(true);
      setShowErrorSection(true);
      return;
    }
    if (!villaDocId) {
      setErrorMessage("Villa data missing (firestoreId). Please refresh and try again.");
      setShowBookingPopup(true);
      setShowErrorSection(true);
      return;
    }
    setShowBookingPopup(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setValidationError("");
  };

  const handleApplicant2Toggle = (e) => {
    setHasApplicant2(e.target.checked);
    if (!e.target.checked) {
      setFormData((prev) => ({ ...prev, applicant2Name: "", applicant2Pan: "" }));
      setAadhaar2Url("");
      setPan2Url("");
    }
  };

  const generateBookingNumber = async () => {
    const counterRef = doc(db, "counters", "villaBookingCounter");
    const prefix = "VILLA/2025-26/";

    const code = await runTransaction(db, async (transaction) => {
      const counterSnap = await transaction.get(counterRef);
      let nextNumber = 1;

      if (counterSnap.exists()) {
        const current = counterSnap.data().lastNumber || 0;
        nextNumber = current + 1;
        if (nextNumber > 9999) throw new Error("Booking limit reached for 2025-26. Contact admin.");
      }

      transaction.set(counterRef, { lastNumber: nextNumber }, { merge: true });
      const formatted = String(nextNumber).padStart(4, "0");
      return `${prefix}${formatted}`;
    });

    return code;
  };

  const handleProceedToPayment = async (e) => {
    e.preventDefault();
    setValidationError("");

    if (!razorpayLoaded || !window.Razorpay) {
      setErrorMessage("Payment gateway failed to load. Please try again later.");
      setShowErrorSection(true);
      return;
    }

    if (!formData.termsAgreed || !formData.privacyAgreed) {
      setValidationError("Please agree to the Terms and Conditions and Refund Policy.");
      return;
    }

    if (!formData.name || !formData.email || !formData.mobileNumber || !formData.pan) {
      setValidationError("Please fill all required fields for Applicant 1, including PAN.");
      return;
    }

    if (hasApplicant2 && (!formData.applicant2Name || !formData.applicant2Pan)) {
      setValidationError("Please fill all required fields for Applicant 2, including PAN.");
      return;
    }

    if (
      (formData.source === "Channel Partner" || formData.source === "Existing Customer Referral") &&
      !formData.sourceDetails
    ) {
      setValidationError("Please provide details for Channel Partner or Referral.");
      return;
    }

    setShowKYCSection(true);
    setShowErrorSection(false);
  };

  const handleProceedFromKYC = () => {
    if (!aadhaarUrl || !panUrl) {
      setValidationError("Please upload both Aadhaar and PAN photos for Applicant 1.");
      return;
    }
    if (hasApplicant2 && (!aadhaar2Url || !pan2Url)) {
      setValidationError("Please upload both Aadhaar and PAN photos for Applicant 2.");
      return;
    }

    setShowKYCSection(false);
    setShowConfirmationSection(true);
  };

  const uploadToCloudinary = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("upload_preset", uploadPreset);

    try {
      const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (data.secure_url) return data.secure_url;
      throw new Error("Upload failed");
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to upload document. Please try again.");
      setShowErrorSection(true);
      return null;
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadToCloudinary(file);
    if (!url) return;

    if (type === "aadhaar") setAadhaarUrl(url);
    if (type === "pan") setPanUrl(url);
    if (type === "aadhaar2") setAadhaar2Url(url);
    if (type === "pan2") setPan2Url(url);
  };

  const sendConfirmationEmail = async ({
  email,
  name,
  applicant2Name,
  bookingNumber,
  villaNumber,
  villaId,
  pan,
  applicant2Pan,
  source,
  sourceDetails,

  plotAreaSqft,
  builtUpAreaSqft,
  villaType,
  villaPrice,
}) => {
  try {
    const response = await fetch("/.netlify/functions/send-email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        name,
        applicant2Name,
        bookingNumber,
        villaNumber,
        villaId,
        plotAreaSqft,
        builtUpAreaSqft,
        villaType,
        villaPrice,
        pan,
        applicant2Pan,
        source,
        sourceDetails: sourceDetails || "",
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      console.error("Email failed:", err);
    }
  } catch (error) {
    console.error("Error sending confirmation email:", error);
  }
};

  const revertVillaStatus = async () => {
    if (!villaDocId) return;
    try {
      await updateDoc(doc(db, VILLA_COLLECTION, villaDocId), { Status: "Available" });
      setPlotStatus?.("Available");
    } catch (err) {
      console.error("Error reverting villa status:", err);
    }
  };

  // ---------------------- PAYMENT CORE ----------------------
  const handleConfirmDetails = async () => {
    setIsPaymentProcessing(true);
    setShowBookingPopup(false);

    let bookingNumber = "";
    let bookingDocId = "";

    try {
      bookingNumber = await generateBookingNumber();

      // 1) Lock villa: Available -> Booking
      await runTransaction(db, async (transaction) => {
        const villaRef = doc(db, VILLA_COLLECTION, villaDocId);
        const snap = await transaction.get(villaRef);

        if (!snap.exists()) throw new Error("Villa does not exist.");

        const currentStatus = safeLower(snap.data().Status);
        if (currentStatus !== "available") throw new Error("Villa is not available for booking.");

        transaction.update(villaRef, {
          Status: "Booking",
          bookingTimestamp: new Date().toISOString(),
          bookingNumber,
        });
      });

      // 2) Create booking doc (Pending)
      const kycDocuments = [
        { type: "aadhaar", url: aadhaarUrl },
        { type: "pan", url: panUrl },
        ...(hasApplicant2
          ? [
              { type: "aadhaar2", url: aadhaar2Url },
              { type: "pan2", url: pan2Url },
            ]
          : []),
      ];

      const bookingDoc = await addDoc(collection(db, BOOKINGS_COLLECTION), {
        userId: auth.currentUser?.uid || "anonymous",
        bookingNumber,
        villaFirestoreId: villaDocId,
        villaId: plotData?.villaId || projectId,
        villaNumber: plotData?.villaNumber || "N/A",
        facing: plotData?.facing || "N/A",
        configuration: plotData?.configuration || "N/A",
        builtUpAreaSqft: plotData?.builtUpAreaSqft ?? null,
        landAreaSqft: plotData?.landAreaSqft ?? null,
        inclusivePrice: plotData?.inclusivePrice ?? null,
        premiumCharges: plotData?.premiumCharges ?? null,
        advanceAmountPaise,
        paymentStatus: "Pending",
        applicant1: {
          name: formData.name,
          email: formData.email,
          mobileNumber: formData.mobileNumber,
          pan: formData.pan,
        },
        applicant2: hasApplicant2
          ? { name: formData.applicant2Name, pan: formData.applicant2Pan }
          : null,
        source: formData.source,
        sourceDetails: formData.source === "Direct" ? "" : formData.sourceDetails,
        kycDocuments,
        createdAt: serverTimestamp(),
      });

      bookingDocId = bookingDoc.id;

      // 3) Timeout revert (10 min)
      timeoutIdRef.current = setTimeout(async () => {
        await revertVillaStatus();
        await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingDocId), {
          paymentStatus: "Failed",
          failReason: "Timeout - payment not completed",
          updatedAt: serverTimestamp(),
        });
      }, 10 * 60 * 1000);

      // 4) Razorpay
      const options = {
        key: process.env.REACT_APP_FRAZORPAY_API_KEY,
        amount: advanceAmountPaise, // ✅ PAISE
        currency: "INR",
        name: "Clarks Exotica Villas",
        description: `Advance payment for Villa ${plotData?.villaNumber || projectId}`,
        handler: async () => {
          try {
            if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

            // Mark booking success
            await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingDocId), {
              paymentStatus: "Success",
              updatedAt: serverTimestamp(),
            });

            // Villa -> Booked
            await updateDoc(doc(db, VILLA_COLLECTION, villaDocId), {
              Status: "Booked",
              bookedAt: serverTimestamp(),
              bookingNumber,
            });

            // Invoice record
            await addDoc(collection(db, INVOICES_COLLECTION), {
              bookingId: bookingDocId,
              bookingNumber,
              villaFirestoreId: villaDocId,
              villaId: plotData?.villaId || projectId,
              villaNumber: plotData?.villaNumber || "N/A",
              advanceAmountPaise,
              status: "Success",
              timestamp: new Date().toISOString(),
              applicant1: {
                name: formData.name,
                email: formData.email,
                mobileNumber: formData.mobileNumber,
                pan: formData.pan,
              },
              applicant2: hasApplicant2
                ? { name: formData.applicant2Name, pan: formData.applicant2Pan }
                : null,
              source: formData.source,
              sourceDetails: formData.source === "Direct" ? "" : formData.sourceDetails,
            });

            // Email
            await sendConfirmationEmail({
  email: formData.email,
  name: formData.name,
  applicant2Name: hasApplicant2 ? formData.applicant2Name : "",
  bookingNumber,
  villaNumber: plotData?.villaNumber || "N/A",
  villaId: plotData?.villaId || projectId,

  plotAreaSqft: plotData?.landAreaSqft ?? "N/A",
  builtUpAreaSqft: plotData?.builtUpAreaSqft ?? "N/A",
  villaType: plotData?.configuration || "N/A",
  villaPrice: plotData?.inclusivePrice ?? "N/A",

  pan: formData.pan,
  applicant2Pan: hasApplicant2 ? formData.applicant2Pan : "",
  source: formData.source,
  sourceDetails: formData.source === "Direct" ? "" : formData.sourceDetails,
});


            setBookingDetails({
              bookingId: bookingDocId,
              bookingNumber,
              advanceAmount: advanceAmountRupees,
              email: formData.email,
              mobileNumber: formData.mobileNumber,
              pan: formData.pan,
              applicant2Pan: hasApplicant2 ? formData.applicant2Pan : "",
              source: formData.source,
              sourceDetails: formData.source === "Direct" ? "" : formData.sourceDetails,
            });

            setIsPaymentProcessing(false);
            setShowConfirmationSection(false);
            setShowConfirmation(true);
            setPlotStatus?.("Booked");
          } catch (err) {
            console.error("Error after payment success:", err);
            await revertVillaStatus();
            await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingDocId), {
              paymentStatus: "Failed",
              failReason: "Payment success but DB update failed",
              updatedAt: serverTimestamp(),
            });

            setIsPaymentProcessing(false);
            setErrorMessage("Payment successful, but failed to save booking. Please contact support.");
            setShowBookingPopup(true);
            setShowConfirmationSection(false);
            setShowErrorSection(true);
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.mobileNumber,
        },
        notes: {
          villaFirestoreId: villaDocId,
          villaId: plotData?.villaId || projectId,
          villaNumber: plotData?.villaNumber || "N/A",
          bookingNumber,
          pan: formData.pan,
          applicant2Pan: hasApplicant2 ? formData.applicant2Pan : "",
          source: formData.source,
          sourceDetails: formData.source === "Direct" ? "" : formData.sourceDetails,
        },
        theme: { color: "#024837" },
        modal: {
          ondismiss: async () => {
            setIsPaymentProcessing(false);
            if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

            try {
              await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingDocId), {
                paymentStatus: "Failed",
                failReason: "User cancelled Razorpay",
                updatedAt: serverTimestamp(),
              });
              await revertVillaStatus();

              await addDoc(collection(db, INVOICES_COLLECTION), {
                bookingId: bookingDocId,
                bookingNumber,
                villaFirestoreId: villaDocId,
                villaId: plotData?.villaId || projectId,
                villaNumber: plotData?.villaNumber || "N/A",
                advanceAmountPaise,
                status: "Failed",
                BookingStatus: "Booking Cancelled",
                timestamp: new Date().toISOString(),
              });

              setErrorMessage("Payment was cancelled. You can try again.");
              setShowBookingPopup(true);
              setShowConfirmationSection(false);
              setShowErrorSection(true);
            } catch (err) {
              console.error("Cancel flow error:", err);
              setErrorMessage("Failed to process cancellation. Please contact support.");
              setShowBookingPopup(true);
              setShowConfirmationSection(false);
              setShowErrorSection(true);
            }
          },
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", async (response) => {
        setIsPaymentProcessing(false);
        if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

        try {
          await updateDoc(doc(db, BOOKINGS_COLLECTION, bookingDocId), {
            paymentStatus: "Failed",
            failReason: response?.error?.description || "Payment failed",
            updatedAt: serverTimestamp(),
          });

          await revertVillaStatus();

          await addDoc(collection(db, INVOICES_COLLECTION), {
            bookingId: bookingDocId,
            bookingNumber,
            villaFirestoreId: villaDocId,
            villaId: plotData?.villaId || projectId,
            villaNumber: plotData?.villaNumber || "N/A",
            advanceAmountPaise,
            status: "Failed",
            BookingStatus: "Booking Failed",
            timestamp: new Date().toISOString(),
          });

          setErrorMessage(response?.error?.description || "Payment failed due to an unknown error.");
          setShowBookingPopup(true);
          setShowConfirmationSection(false);
          setShowErrorSection(true);
        } catch (err) {
          console.error("Payment failed handler error:", err);
          setErrorMessage("Payment failed and could not update status. Please contact support.");
          setShowBookingPopup(true);
          setShowConfirmationSection(false);
          setShowErrorSection(true);
        }
      });

      rzp.open();
    } catch (err) {
      console.error("Error initiating booking:", err);
      setIsPaymentProcessing(false);
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);

      setErrorMessage(err?.message || "Failed to initiate booking. Villa may not be available.");
      setShowBookingPopup(true);
      setShowConfirmationSection(false);
      setShowErrorSection(true);
    }
  };

  const handlePhoneClick = () => {
    window.location.href = "tel:+918880555111";
  };

  const handleRetryBooking = async () => {
    setErrorMessage("");
    setValidationError("");

    try {
      if (!villaDocId) {
        setErrorMessage("Villa data missing (firestoreId). Please refresh and try again.");
        setShowErrorSection(true);
        return;
      }

      const snap = await getDoc(doc(db, VILLA_COLLECTION, villaDocId));
      if (!snap.exists() || safeLower(snap.data().Status) !== "available") {
        setErrorMessage("Villa is not available for booking.");
        setShowErrorSection(true);
        return;
      }

      setShowErrorSection(false);
      setFormData((prev) => ({
        ...prev,
        termsAgreed: false,
        privacyAgreed: false,
        pan: "",
        applicant2Name: "",
        applicant2Pan: "",
        source: "Direct",
        sourceDetails: "",
      }));
      setAadhaarUrl("");
      setPanUrl("");
      setAadhaar2Url("");
      setPan2Url("");
      setHasApplicant2(false);
    } catch (err) {
      console.error("Retry check failed:", err);
      setErrorMessage("Failed to verify villa availability. Please try again later.");
      setShowErrorSection(true);
    }
  };

  const handleReturnToPlotPage = () => {
    setShowBookingPopup(false);
    closeParentPopup?.();
  };

  const generateReceiptPDF = async () => {
  if (!bookingDetails?.bookingNumber) return;

  const pdf = new jsPDF({ unit: "mm", format: "a4" });

  // ----------------------------
  // Layout constants
  // ----------------------------
  const pageW = pdf.internal.pageSize.getWidth(); // 210
    const pageH = pdf.internal.pageSize.getHeight(); // 297
    const left = 20;
    const right = pageW - 20;
    const green = [70, 150, 70];
    const lineH = 6; // ⬆ increased line height

    const formatINR = (n) =>
      Number(n || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 0,
      });

    /* =========================
       TEXT HELPER
       ========================= */
    const drawRichText = (parts, x, yStart, maxWidth, lineHeight) => {
      let cx = x;
      let cy = yStart;

      for (const part of parts) {
        pdf.setFont("Helvetica", part.bold ? "bold" : "normal");
        pdf.setFontSize(part.size || 12); // ⬆ font size increased

        const tokens = String(part.text || "").split(/(\s+)/);
        for (let token of tokens) {
          if (!token) continue;

          const lines = token.split("\n");
          for (let i = 0; i < lines.length; i++) {
            const seg = lines[i];
            if (seg) {
              const w = pdf.getTextWidth(seg);
              if (cx + w > x + maxWidth) {
                cx = x;
                cy += lineHeight;
              }
              pdf.text(seg, cx, cy);
              cx += w;
            }
            if (i < lines.length - 1) {
              cx = x;
              cy += lineHeight;
            }
          }
        }
      }
      return cy + lineHeight;
    };

    /* =========================
       DATA
       ========================= */
    const receiptNo =
      bookingDetails?.receiptNo ||
      bookingDetails?.receiptNumber ||
      bookingDetails?.bookingNumber;

    const receiptDate =
      bookingDetails?.receiptDate ||
      new Date().toLocaleDateString("en-IN");

    const buyerName = formData?.name || "N/A";
    const buyerMobile = formData?.mobileNumber || "";
    const buyerMail = formData?.email || "";
    const buyerAddress = formData?.address || "";

    // ✅ FIXED AMOUNT
    const advanceAmount = 99999;
    const advanceWords =
      "Ninety Nine Thousand Nine Hundred Ninety Nine Only";

    const paymentRef =
      bookingDetails?.paymentRef ||
      bookingDetails?.razorpayPaymentId ||
      bookingDetails?.transactionId ||
      "";

    const paymentDateText =
      bookingDetails?.paymentDateText ||
      bookingDetails?.paymentDate ||
      receiptDate;

    const projectName =
      plotData?.projectName || "Clarks Exotica Sakleshpur – 573134";
    const villaNo = plotData?.villaNumber || plotData?.villaNo || "N/A";
    const plotSqft = plotData?.landAreaSqft ?? "N/A";
    const builtUpSqft = plotData?.builtUpAreaSqft ?? "N/A";

    const footerAddress =
      bookingDetails?.footerAddress ||
      "#40/1, 1st Floor , 30th Main Road, Jayanagar 7th Block, Bangalore 560041";

    /* =========================
       HEADER
       ========================= */
    const logoImageUrl =
      "/mlalogo.png";

    try {
      pdf.addImage(logoImageUrl, "PNG", 165, 12, 25, 20);
    } catch {}

    pdf.setDrawColor(...green);
    pdf.setLineWidth(0.4);
    pdf.line(left, 36, right, 36);

    /* =========================
       TITLE
       ========================= */
    pdf.setFont("Helvetica", "bold");
    pdf.setFontSize(16); // ⬆
    pdf.text("RECEIPT", pageW / 2, 50, { align: "center" });

    pdf.setFontSize(12); // ⬆
    pdf.text(`Receipt No. ${receiptNo}`, left, 62);
    pdf.text(`Date : ${receiptDate}`, right, 62, { align: "right" });

    /* =========================
       TO BLOCK
       ========================= */
    let y = 78;

    pdf.setFontSize(12);
    pdf.setFont("Helvetica", "bold");
    pdf.text("To,", left, y);
    y += lineH;

    pdf.setFont("Helvetica", "normal");
    pdf.text(`Mr. ${buyerName}`, left, y);
    y += lineH;

    if (buyerMobile) {
      pdf.text(`Mobile: ${buyerMobile}`, left, y);
      y += lineH;
    }

    if (buyerMail) {
      pdf.text(`Email: ${buyerMail}`, left, y);
      y += lineH;
    }

    if (buyerAddress) {
      const addrLines = buyerAddress.split("\n");
      pdf.text(addrLines, left, y);
      y += addrLines.length * lineH;
    }

    /* =========================
       PARAGRAPH 1
       ========================= */
    y += 16;
    const paraX = left + 10;
    const paraW = pageW - 2 * (left + 10);

    const onlineText = paymentRef
      ? `via Online Payment (Ref: ${paymentRef})`
      : "via Online Payment";

    const paragraph1 = [
      {
        text:
          "We hereby acknowledge the receipt of your payment for an amount of\n",
        bold: false,
      },
      {
        text: `Rs.${formatINR(advanceAmount)}/- (${advanceWords}) `,
        bold: true,
      },
      {
        text: `${onlineText} on ${paymentDateText} towards your booking of villa at`,
        bold: false,
      },
    ];

    y = drawRichText(paragraph1, paraX, y, paraW, lineH);

    /* ✅ GAP BETWEEN PARAGRAPHS */
    y += 10;

    /* =========================
       PARAGRAPH 2 (PROJECT)
       ========================= */
    const paragraph2 = [
      { text: `${projectName}\n`, bold: true },
      { text: `bearing Villa No. ${villaNo}, Plot\n`, bold: false },
      {
        text: `Measuring ${plotSqft} sqft and built up of ${builtUpSqft} Sqft.`,
        bold: true,
      },
    ];

    y = drawRichText(paragraph2, paraX, y, paraW, lineH);

    /* =========================
       SYSTEM NOTE
       ========================= */
    const noteY = 230;

    pdf.setFont("Helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text("For MLA Estates", right, noteY, { align: "right" });

    pdf.setFont("Helvetica", "normal");
    pdf.setFontSize(10);
    pdf.text(
      "This is a system-generated receipt and does not require a signature.",
      right,
      noteY + 14,
      { align: "right" }
    );

    /* =========================
       FOOTER
       ========================= */
    pdf.setDrawColor(...green);
    pdf.setLineWidth(0.4);
    pdf.line(left, pageH - 22, right, pageH - 22);

    pdf.setFontSize(10);
    pdf.text(footerAddress, pageW / 2, pageH - 12, {
      align: "center",
    });

    pdf.save(`Receipt_${receiptNo}.pdf`);
};


  // ---------------------- UI ----------------------
  const statusLower = safeLower(plotStatus);

  return (
    <>
      {/* PROCESSING OVERLAY */}
      {isPaymentProcessing && (
        <div
          className="payment-processing-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 5000,
          }}
        >
          <div
            className="payment-processing-section"
            style={{
              textAlign: "center",
              padding: "30px 40px",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            }}
          >
            <div
              className="circular-loader"
              style={{
                border: "5px solid #f3f3f3",
                borderTop: "5px solid #000000",
                borderRadius: "50%",
                width: "50px",
                height: "50px",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <h4 style={{ margin: "0 0 10px", color: "#000000" }}>Finalising Your Booking</h4>
            <p style={{ margin: 0, color: "#555" }}>
              Please wait while we secure your villa and generate the receipt…
            </p>
            <style>{`
              @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
          </div>
        </div>
      )}

      {statusLower === "available" ? (
        <div className="smallText">
          <p>
            <i className="fas fa-phone"></i> <strong>+91 7019299769</strong> |{" "}
            <i className="fas fa-envelope"></i> <strong> crm@landandkey.in</strong>
          </p>
        </div>
      ) : null}

      {statusLower === "available" ? (
        <div className="book-now-button" onClick={handleBookNow} title="Click to book this villa">
          <i className="fas fa-book book-now-icon" style={{ marginRight: "8px", transition: "transform 0.2s" }} />
          Reserve Now
          <span
            className="badge"
            style={{
              marginLeft: "8px",
              background: "none",
              border: "2px dotted #f5fde9",
              color: "#f5fde9",
              padding: "8px 8px",
              borderRadius: "15px",
              fontSize: "12px",
            }}
          >
            <strong>Available</strong>
          </span>
        </div>
      ) : (
        <>
          <div className="contact-support">
            <span>
              <strong>Need Support:</strong>
            </span>
            <button onClick={handlePhoneClick} className="contacts-button">
              <i className="fas fa-phone-alt"></i> +91 8880555111
            </button>
          </div>
          <div className="not-available">Currently Unavailable</div>
        </>
      )}

      {/* BOOKING POPUP */}
      {showBookingPopup && !isPaymentProcessing && (
        <div className="booking-popup" onClick={() => setShowBookingPopup(false)}>
          <div className="booking-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="booking-popup-columns">
              {/* LEFT INFO */}
              <div className="booking-column booking-info">
                <img
                  src="./mlalogo.png"
                  alt="Logo"
                  className="company-logo"
                  style={{ maxWidth: "110px", marginBottom: "30px", borderRadius: "16px", alignSelf: "flex-start" }}
                />

                <h4 className="booking info-item">
                  <i className="fas fa-check-circle"></i> Villa Details (Verify)
                </h4>

                <div className="info-item">
                  Villa Number: <strong>{plotData?.villaNumber || "N/A"}</strong>
                </div>
                <div className="info-item">
                  Facing: <strong>{plotData?.facing || "N/A"}</strong>
                </div>
                <div className="info-item">
                  Configuration: <strong>{plotData?.configuration || "N/A"}</strong>
                </div>
                <div className="info-item">
                  Built-up: <strong>{plotData?.builtUpAreaSqft ?? "N/A"} sqft</strong>
                </div>
                <div className="info-item">
                  Land: <strong>{plotData?.landAreaSqft ?? "N/A"} sqft</strong>
                </div>
                <div className="info-item">
                  Final Price: <strong>{formatINR(plotData?.inclusivePrice)}</strong>
                </div>

                <h4 className="booking" style={{ textAlign: "center" }}>
                  <i className="fas fa-credit-card"></i> Token: {formatINR(advanceAmountRupees)}
                </h4>

                <div className="payment-methods" style={{ textAlign: "center" }}>
                  <p>Accepted Payment Methods</p>
                  <div className="payment-icons">
                    <i className="fab fa-cc-visa" style={{ fontSize: "24px", marginRight: "10px" }}></i>
                    <i className="fab fa-cc-mastercard" style={{ fontSize: "24px", marginRight: "10px" }}></i>
                    <i className="fa-brands fa-google-pay" style={{ fontSize: "24px", marginRight: "10px" }}></i>
                    <i className="fab fa-cc-amex" style={{ fontSize: "24px" }}></i>
                  </div>
                  <p className="secure-note" style={{ color: "black" }}>
                    <i className="fas fa-lock" style={{ marginRight: "5px", color: "black" }}></i>
                    Secure payment powered by Razorpay
                  </p>
                </div>
              </div>

              {/* RIGHT FORM */}
              <div className="booking-column booking-form">
                {showErrorSection ? (
                  <div className="error-section">
                    <h4>
                      <i className="fas fa-exclamation-circle" style={{ color: "#d32f2f" }}></i> Payment Error
                    </h4>
                    <div className="error-details">
                      <p style={{ fontSize: "15px" }}>{errorMessage}</p>
                      <p style={{ fontSize: "12px" }}>
                        For assistance, contact{" "}
                        <a href="mailto:crmg@landandkey.in">crm@landandkey.in</a> or{" "}
                        <a href="tel:+917019299769">+91-7019299769</a>.
                      </p>
                    </div>

                    <div className="form-buttons" style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                      <button
                        className="retry-button"
                        onClick={handleRetryBooking}
                        style={{
                          padding: "20px 30px",
                          background: "#024837",
                          color: "white",
                          border: "none",
                          borderRadius: "18px",
                          cursor: "pointer",
                        }}
                      >
                        Retry
                      </button>

                      <button
                        className="return-button"
                        onClick={handleReturnToPlotPage}
                        style={{
                          padding: "20px 30px",
                          background: "#f5f7e9",
                          color: "black",
                          border: "1px dotted black",
                          borderRadius: "18px",
                          cursor: "pointer",
                        }}
                      >
                        Return to Map
                      </button>
                    </div>
                  </div>
                ) : showConfirmationSection ? (
                  <div className="confirmation-section">
                    <h4>
                      <i className="fas fa-info-circle"></i> Confirm Your Details
                    </h4>
                    <p>Please verify the information before proceeding.</p>

                    <div
                      className="confirmation-details"
                      style={{
                        padding: "10px",
                        border: "2px dotted #000000",
                        borderRadius: "26px",
                        marginBottom: "20px",
                      }}
                    >
                      <p style={{ margin: "5px 0", color: "black" }}>
                        Applicant 1 Name: <strong>{formData.name}</strong>
                      </p>
                      {hasApplicant2 && (
                        <p style={{ margin: "5px 0", color: "black" }}>
                          Applicant 2 Name: <strong>{formData.applicant2Name}</strong>
                        </p>
                      )}
                      <p style={{ margin: "5px 0", color: "black" }}>
                        Email: <strong>{formData.email}</strong>
                      </p>
                      <p style={{ margin: "5px 0", color: "black" }}>
                        Mobile: <strong>{formData.mobileNumber}</strong>
                      </p>
                      <p style={{ margin: "5px 0", color: "black" }}>
                        PAN: <strong>{formData.pan}</strong>
                      </p>
                      {hasApplicant2 && (
                        <p style={{ margin: "5px 0", color: "black" }}>
                          Applicant 2 PAN: <strong>{formData.applicant2Pan}</strong>
                        </p>
                      )}
                      <p style={{ margin: "5px 0", color: "black" }}>
                        Source: <strong>{formData.source}</strong>
                      </p>
                      {formData.source !== "Direct" && formData.sourceDetails && (
                        <p style={{ margin: "5px 0", color: "black" }}>
                          Source Details: <strong>{formData.sourceDetails}</strong>
                        </p>
                      )}
                    </div>

                    <div className="form-buttons" style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                      <button
                        className="confirm-button"
                        onClick={handleConfirmDetails}
                        style={{
                          padding: "10px 20px",
                          background: "#000000",
                          color: "white",
                          border: "none",
                          borderRadius: "18px",
                          cursor: "pointer",
                        }}
                      >
                        Confirm & Pay
                      </button>

                      <button
                        className="edit-button"
                        onClick={() => setShowConfirmationSection(false)}
                        style={{
                          padding: "10px 20px",
                          background: "#f5f7e9",
                          color: "black",
                          border: "1px dotted black",
                          borderRadius: "18px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ) : showKYCSection ? (
                  <div className="kyc-section" style={{ padding: "10px", margin: "0", maxWidth: "300px" }}>
                    <h4 style={{ fontSize: "16px", margin: "5px 0", display: "flex", alignItems: "center" }}>
                      <i className="fas fa-id-card" style={{ marginRight: "5px" }}></i> KYC Verification
                    </h4>
                    <p style={{ fontSize: "12px", margin: "5px 0" }}>Upload Aadhaar and PAN photos.</p>

                    {validationError && (
                      <div style={{ color: "#d32f2f", fontSize: "12px", margin: "5px 0", textAlign: "center" }}>
                        {validationError}
                      </div>
                    )}

                    {/* Applicant 1 */}
                    <div style={{ margin: "5px 0" }}>
                      <h5 style={{ fontSize: "14px", margin: "3px 0" }}>Applicant 1 Aadhaar</h5>
                      <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, "aadhaar")} />
                      {aadhaarUrl && <img src={aadhaarUrl} alt="Aadhaar Preview" style={{ maxWidth: "100px", maxHeight: "60px", marginTop: "5px", objectFit: "contain" }} />}
                    </div>

                    <div style={{ margin: "5px 0" }}>
                      <h5 style={{ fontSize: "14px", margin: "3px 0" }}>Applicant 1 PAN</h5>
                      <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, "pan")} />
                      {panUrl && <img src={panUrl} alt="PAN Preview" style={{ maxWidth: "100px", maxHeight: "60px", marginTop: "5px", objectFit: "contain" }} />}
                    </div>

                    {/* Applicant 2 */}
                    {hasApplicant2 && (
                      <>
                        <div style={{ margin: "5px 0" }}>
                          <h5 style={{ fontSize: "14px", margin: "3px 0" }}>Applicant 2 Aadhaar</h5>
                          <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, "aadhaar2")} />
                          {aadhaar2Url && <img src={aadhaar2Url} alt="Aadhaar2 Preview" style={{ maxWidth: "100px", maxHeight: "60px", marginTop: "5px", objectFit: "contain" }} />}
                        </div>

                        <div style={{ margin: "5px 0" }}>
                          <h5 style={{ fontSize: "14px", margin: "3px 0" }}>Applicant 2 PAN</h5>
                          <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, "pan2")} />
                          {pan2Url && <img src={pan2Url} alt="PAN2 Preview" style={{ maxWidth: "100px", maxHeight: "60px", marginTop: "5px", objectFit: "contain" }} />}
                        </div>
                      </>
                    )}

                    <div className="form-buttons">
                      <button className="proceed-button" onClick={handleProceedFromKYC}>
                        Proceed to Confirmation
                      </button>
                      <button className="cancel-button" onClick={() => setShowKYCSection(false)}>
                        Back
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h4>Enter Information</h4>

                    {validationError && (
                      <div style={{ color: "#d32f2f", fontSize: "14px", marginBottom: "10px", textAlign: "center" }}>
                        {validationError}
                      </div>
                    )}

                    <div className="form-group">
                      <input
                        type="text"
                        name="name"
                        placeholder="Applicant 1 Name"
                        value={formData.name}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="checkbox-group">
                      <label>
                        <input type="checkbox" checked={hasApplicant2} onChange={handleApplicant2Toggle} />
                        Add Applicant 2
                      </label>
                    </div>

                    {hasApplicant2 && (
                      <div className="form-group">
                        <input
                          type="text"
                          name="applicant2Name"
                          placeholder="Applicant 2 Name"
                          value={formData.applicant2Name}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <input
                        type="tel"
                        name="mobileNumber"
                        placeholder="Mobile Number"
                        value={formData.mobileNumber}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <input
                        type="text"
                        name="pan"
                        placeholder="Applicant 1 PAN Number"
                        value={formData.pan}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    {hasApplicant2 && (
                      <div className="form-group">
                        <input
                          type="text"
                          name="applicant2Pan"
                          placeholder="Applicant 2 PAN Number"
                          value={formData.applicant2Pan}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                    )}

                    <span className="form-group" style={{ fontSize: "14px", fontWeight: "900", color: "gray" }}>
                      Source *
                    </span>

                    <div className="form-group">
                      <select
                        name="source"
                        value={formData.source}
                        onChange={handleFormChange}
                        required
                        style={{ width: "100%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
                      >
                        <option value="Direct">Direct</option>
                        <option value="Channel Partner">Channel Partner</option>
                        <option value="Existing Customer Referral">Existing Customer Referral</option>
                      </select>
                    </div>

                    {formData.source !== "Direct" && (
                      <div className="form-group">
                        <input
                          type="text"
                          name="sourceDetails"
                          placeholder={formData.source === "Channel Partner" ? "Channel Partner Name" : "Referral Name"}
                          value={formData.sourceDetails}
                          onChange={handleFormChange}
                          required
                          style={{ width: "90%", padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
                        />
                      </div>
                    )}

                    <div className="checkbox-group">
                      <label>
                        <input type="checkbox" name="termsAgreed" checked={formData.termsAgreed} onChange={handleFormChange} />
                        I agree to the Terms and Conditions
                      </label>
                    </div>

                    <div className="checkbox-group">
                      <label>
                        <input type="checkbox" name="privacyAgreed" checked={formData.privacyAgreed} onChange={handleFormChange} />
                        I agree to the Refund Policy
                      </label>
                    </div>

                    <div className="form-buttons">
                      <button type="submit" className="proceed-button" onClick={handleProceedToPayment}>
                        Proceed to KYC
                      </button>
                      <button type="button" className="cancel-button" onClick={() => setShowBookingPopup(false)}>
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FINAL CONFIRMATION */}
      {showConfirmation && (
        <div className="confirmation-popup" onClick={closeParentPopup}>
          <div className="confirmation-popup-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              <i className="fas fa-check-circle"></i> Booking Confirmed!
            </h3>

            <div className="confirmation-details">
              <p style={{  fontWeight: "600", fontSize: "18px" }}>
                Booking Number: <strong>{bookingDetails?.bookingNumber}</strong>
              </p>

              <p>
                Token Amount: <strong>{formatINR(bookingDetails?.advanceAmount)}</strong>
              </p>

              <p>
                Villa Number: <strong>{plotData?.villaNumber}</strong>
              </p>

              <p>
                <strong>{formData.name}</strong>
                {hasApplicant2 && formData.applicant2Name && (
                  <span>
                    {" "}
                    | <strong>{formData.applicant2Name}</strong>
                  </span>
                )}
                <span>
                  {" "}
                  | <strong>{formData.email}</strong> | <strong>{formData.pan}</strong>
                </span>
                {hasApplicant2 && formData.applicant2Pan && (
                  <span>
                    {" "}
                    | <strong>{formData.applicant2Pan}</strong>
                  </span>
                )}
              </p>

              <p style={{ padding: "6px", fontWeight: "400" }}>
                Source: <strong>{bookingDetails?.source}</strong>
                {bookingDetails?.source !== "Direct" && bookingDetails?.sourceDetails && (
                  <span>
                    {" "}
                    | <strong>{bookingDetails.sourceDetails}</strong>
                  </span>
                )}
              </p>

              <p
                style={{
                  padding: "6px",
                  fontWeight: "400",
                  border: "2px dotted green",
                  borderRadius: "15px",
                  color: "green",
                }}
              >
                Confirmation has been sent to <strong>{formData.email}</strong>.
                <br />
                Please check your inbox.
              </p>

              <p>
                Support: <a href="mailto:booking@clarksexoticasakleshpur.com">crm@landandkeys.in</a> <br/> +91-70027-27965
              </p>
            </div>

            <button className="download-receipt-button" onClick={generateReceiptPDF}>
              <i className="fas fa-download"></i> Download Receipt
            </button>

            <button className="download-receipt-button" onClick={closeParentPopup}>
              <i className="fas fa-house"></i>  Home
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentHandler;
